/*
 * @providesModule PlayerLevelsList
 */

import React, {useState} from 'react';

import {_m} from 'INTL';
import Sizes from 'Sizes';

import {
  VirtualizedList,
  View,
} from 'react-native';
import PlayerLevelCell, {IPlayerLevelCellProps} from './PlayerLevelCell';
import PlayerLevelExpandedCell, {IPlayerLevelExpandedCellProps} from './PlayerLevelExpandedCell';
import styles from './PlayerLevelsList.style';

const NO_INDEX_EXPANDED = -1;
export const NUM_COLUMNS = Sizes.select({
  portrait: 3,
  landscape: 5,
});
//exported for test purpose only
export const MIN_NUM_LINE = 5;
//exported for test purpose only
export const MIN_CELLS = NUM_COLUMNS * 5; // eslint-disable-line no-magic-numbers
//exported for test purpose only
export const PROGRESS_LEVEL_SIZE = Sizes.select({portraitTablet: 118, other: 90});

type CellType = Pick<IPlayerLevelCellProps, 'animated'|'centerImage'|'levels'|'title'|'subtitle'|'onPress'> & {backgroundColorInnerCircle?: string; innerCircleBorderColor?: string};
type ExpansionType = Pick<IPlayerLevelExpandedCellProps, 'level'|'totalExp'>;
export type DataType = {
        cell:CellType
        expansion?:ExpansionType
    }

const isCellType = (item: CellType|ExpansionType): item is CellType => {
  return !!(item as CellType);
};

export interface IPlayerLevelsListProps{
  /** The data in the list */
  datas:DataType[];
  /** The index of the expanded cell which has the info expanded. Nothing expanded by default */
  expandedCellIndexOnOpen?:number;
  /** To know if something is loading (not used at this time as no design for loading state) */
  isLoading?:boolean;
  /** Callback to load more data */
  onLoadMore:()=>void;
}

const PlayerLevelsList = ({datas, expandedCellIndexOnOpen = NO_INDEX_EXPANDED, onLoadMore}:IPlayerLevelsListProps) => {
  const [expandedIndex, setExpandedIndex] = useState(expandedCellIndexOnOpen);

  const getItemCount = () => {
    const numRows = Math.ceil(Math.max(datas.length, MIN_CELLS) / NUM_COLUMNS) * 2;
    //minimum items to show, times 2 for the extansion items
    return numRows;
  };

  /**
   * get the Item of a row
   * @param _
   * @param index the row index
   * @returns the array of item on the row
   */
  const getItem = (_:any, index :number):CellType[]|ExpansionType[] => {
    const items = [];
    if (index % 2 === 0) {
      //row cell
      for (let i = 0; i < NUM_COLUMNS; i++) {
        const dataIndex = Math.floor(index / 2) * NUM_COLUMNS + i;
        const item = datas[dataIndex]?.cell;
        if (item) {
          items.push(item);
        } else {
          //push empty cell
          items.push({} as CellType);
        }
      }
      return items;
    } else {
      //row expansion
      for (let i = 0; i < NUM_COLUMNS; i++) {
        const dataIndex = Math.floor(index / 2) * NUM_COLUMNS + i;
        const item = datas[dataIndex]?.expansion;
        if (item) {
          items.push(item);
        } else {
          items.push({} as ExpansionType);
        }
      }
      return items;
    }
  };

  /**
   * Render the row
   * @param param index is the row, item is the array of cells or extansions information
   * @returns rendering of the row
   */
  const renderItem = ({item, index}:{item:CellType[]|ExpansionType[], index:number}) => {
    return (
      <View key={index} style={{flexDirection: 'row', justifyContent: 'space-around'}}
            accessible={false} //Accessible false as we want the children to indicate those
            accessibilityLabel={''}
      >
        {item.map((elem, i) => {
          if (index % 2 === 0 && isCellType(elem)) {
            const dataIndex = (Math.floor((index) / 2)) * NUM_COLUMNS + i;
            const handleOnPress = () => {
              if (dataIndex === expandedIndex) {
                //close it by setting the expanded index to no index
                setExpandedIndex(NO_INDEX_EXPANDED);
              } else {
                //set the expanded index to expand
                setExpandedIndex(dataIndex);
              }
            };
            return <PlayerLevelCell key={i}
                                    {...elem}
                                    size={PROGRESS_LEVEL_SIZE}
                                    onPress={elem.onPress || (datas[dataIndex]?.expansion && handleOnPress)}/>;

          } else {
            if (i === 0) { //only render one per row
              //is exanded if the expandedIndex is in the current row
              const isExpanded = expandedIndex >= (Math.floor((index) / 2)) * NUM_COLUMNS && expandedIndex <= (Math.floor((index) / 2)) * NUM_COLUMNS + NUM_COLUMNS - 1;
              return <PlayerLevelExpandedCell key={i}
                                              {...datas[expandedIndex]?.expansion}
                                              expanded={isExpanded}
                                              arrowIndex={expandedIndex % NUM_COLUMNS}
                                              numberColumns={NUM_COLUMNS}/>;
            }
            return null;
          }
        })}
      </View>
    );
  };

  const keyExtractor = (_:any, index:number) => {
    return index.toString();
  };

  return (
    <View style={styles.container}>
      <VirtualizedList initialNumToRender={MIN_NUM_LINE * 2}
                       style={styles.list}
                       data={[]}//will use getItem to get the data instead
                       getItemCount={getItemCount}
                       getItem={getItem}
                       keyExtractor={keyExtractor}
                       renderItem={renderItem}
                       onEndReachedThreshold={.02}
                       onEndReached={onLoadMore}
      />
    </View>
  );
};

export default PlayerLevelsList;
